import { useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Trash2, Plus, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api, LOSS_REASONS } from "@/lib/api";
import { matches, norm } from "@/lib/search";
import { brl, num, parseNum, pct } from "@/lib/format";
import { Button, Chip, Field, Input } from "@/components/kit";

function LossForm({ conf, onClose, invalidate }) {
  const [q, setQ] = useState("");
  const [prod, setProd] = useState(null);
  const [f, setF] = useState({ qty: "", reason: LOSS_REASONS[0], note: "" });
  const found = useMemo(() => {
    return norm(q) ? conf.items.filter((i) => matches(i.product_name, q)).slice(0, 6) : [];
  }, [q, conf.items]);
  const m = useMutation({
    mutationFn: () => api(`/conferences/${conf.id}/losses`, { method: "POST", body: { product_id: prod.product_id, qty: parseNum(f.qty), reason: f.reason, note: f.note } }),
    onSuccess: () => { invalidate(); toast.success("Perda registrada"); onClose(); },
    onError: (e) => toast.error(e.message),
  });
  return (
    <div className="flex flex-col gap-3 rounded-xl p-4 border fade-in" style={{ borderColor: "var(--border)" }} data-testid="loss-form">
      <span className="label">Produto</span>
      {prod ? (
        <div className="flex items-center gap-3 rounded-lg p-3" style={{ background: "var(--brand-tint)" }} data-testid="loss-selected-product">
          <div className="flex-1"><div className="label">{prod.product_name}</div><div className="caption">{prod.sector_name || "Sem setor"} · {brl(prod.unit_cost)}/{prod.unit}</div></div>
          <Button size="sm" variant="ghost" onClick={() => setProd(null)} data-testid="loss-change-product">Trocar</Button>
        </div>
      ) : (
        <>
          <Input placeholder="Buscar produto" value={q} onChange={(e) => setQ(e.target.value)} data-testid="loss-search-input" />
          {found.map((i) => (
            <button key={i.product_id} className="text-left rounded-lg px-3 py-2 hover:bg-[var(--brand-tint)] text-sm font-semibold" onClick={() => setProd(i)} data-testid={`loss-product-${i.product_id}`}>{i.product_name} <span className="caption">({i.unit})</span></button>
          ))}
          {q && !found.length && <span className="caption">Nenhum produto encontrado.</span>}
        </>
      )}
      <Field label={`Quantidade perdida${prod ? ` (${prod.unit})` : ""}`}><Input inputMode="decimal" value={f.qty} onChange={(e) => setF({ ...f, qty: e.target.value })} data-testid="loss-qty-input" /></Field>
      <span className="label">Motivo</span>
      <div className="scroll-x">{LOSS_REASONS.map((r, k) => <Chip key={r} active={f.reason === r} onClick={() => setF({ ...f, reason: r })} data-testid={`loss-reason-${k}`}>{r}</Chip>)}</div>
      <Field label="Observação (opcional)"><Input maxLength={200} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="loss-note-input" /></Field>
      {prod && parseNum(f.qty) > 0 && <span className="caption">Valor da perda: {brl(parseNum(f.qty) * prod.unit_cost)}</span>}
      <div className="flex gap-2 justify-end">
        <Button variant="ghost" onClick={onClose} data-testid="loss-cancel-button">Cancelar</Button>
        <Button disabled={!prod || parseNum(f.qty) <= 0} loading={m.isPending} onClick={() => m.mutate()} data-testid="loss-save-button">Salvar perda</Button>
      </div>
    </div>
  );
}

export default function LossesSection({ conf, editable, invalidate }) {
  const [adding, setAdding] = useState(false);
  const s = conf.summary;
  const bySector = useMemo(() => {
    const acc = {};
    conf.losses.forEach((l) => { const k = l.sector_name || "Sem setor"; acc[k] = acc[k] || { n: 0, v: 0 }; acc[k].n += 1; acc[k].v += l.value; });
    return Object.entries(acc);
  }, [conf.losses]);
  const del = useMutation({
    mutationFn: (id) => api(`/losses/${id}`, { method: "DELETE" }),
    onSuccess: () => { invalidate(); toast.success("Perda removida"); },
    onError: (e) => toast.error(e.message),
  });
  return (
    <div className="card" data-testid="losses-section">
      <div className="flex items-center gap-3">
        <Trash2 size={18} style={{ color: "var(--danger)" }} />
        <div className="flex-1"><div className="h3">Perdas e desperdício</div><div className="caption">Separadas do consumo normal no CMV</div></div>
        <span className="h3 tnum" data-testid="losses-total">{brl(s.losses_total)}</span>
      </div>
      <div className="grid grid-cols-2 gap-3 mt-4">
        <div className="rounded-xl p-3" style={{ background: "var(--surface-alt)" }}><div className="caption">Consumo normal</div><div className="label tnum" data-testid="normal-consumption">{brl(s.normal_consumption)}</div></div>
        <div className="rounded-xl p-3" style={{ background: "var(--surface-alt)" }}><div className="caption">Perdas no CMV</div><div className="label tnum" data-testid="losses-pct">{pct(s.losses_pct)}</div></div>
      </div>
      {bySector.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3" data-testid="losses-by-sector">
          {bySector.map(([k, v]) => <span key={k} className="badge badge-muted">{k} · {v.n} · {brl(v.v)}</span>)}
        </div>
      )}
      <div className="divide mt-3">
        {conf.losses.map((l) => (
          <div key={l.id} className="flex items-center gap-3 py-2.5" data-testid={`loss-${l.id}`}>
            <div className="flex-1 min-w-0">
              <div className="label truncate">{l.product_name} · {num(l.qty)} {l.unit}</div>
              <div className="caption truncate">{l.reason}{l.note ? ` · ${l.note}` : ""} · por {l.created_by}</div>
            </div>
            <span className="label tnum">{brl(l.value)}</span>
            {editable && <button className="icon-btn" onClick={() => del.mutate(l.id)} data-testid={`loss-delete-${l.id}`}><XCircle size={15} /></button>}
          </div>
        ))}
      </div>
      {!conf.losses.length && !adding && <p className="caption mt-3">Nenhuma perda registrada neste período.</p>}
      {adding ? <div className="mt-3"><LossForm conf={conf} onClose={() => setAdding(false)} invalidate={invalidate} /></div> : editable && (
        <Button size="sm" variant="secondary" icon={Plus} className="mt-3" onClick={() => setAdding(true)} data-testid="add-loss-button">Registrar perda</Button>
      )}
    </div>
  );
}
